import { useCallback, useEffect, useMemo, useState } from 'react'
import { Heart, MessageCircle, Trash2, CalendarDays, ListChecks, Sparkles, Send, ClipboardCheck, Newspaper, ChevronDown, ChevronRight, Download, BarChart3, X, Check, Plus, Lock } from 'lucide-react'
import type { FeedActivityDto, MemberDto, OccurrenceDto, ListDto, AppReleaseDto, PollDto } from '@familynest/shared'
import { api } from '../../lib/api'
import { useAuth } from '../../store/auth'
import { useLive } from '../../lib/ws'
import { formatRelative, formatTime, startOfDay, addDays, isSameDay } from '../../lib/date'
import { occurrenceDayRange } from '../calendar/utils'
import { Avatar, Button, ErrorBanner, useBackdropClose } from '../ui'
import { useDialogs } from '../ui/dialogs'
import { personTextColor } from '../../theme/persons'
import { UpdateDialog } from '../UpdateDialog'
import { checkForUpdate } from '../../lib/appUpdate'

const REF_ICON: Record<string, typeof CalendarDays> = {
  event: CalendarDays,
  list: ListChecks,
  app_release: Download,
}

const fieldCls = 'w-full rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]'
const fieldStyle = { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' } as const

/** „Installieren"-Button an App-Update-Feed-Beiträgen — erscheint NUR, wenn wirklich
    eine neuere Version als die installierte vorliegt (nativ; im Web/aktuell nichts). */
function ReleaseInstallButton(): React.JSX.Element | null {
  const [rel, setRel] = useState<AppReleaseDto | null>(null)
  const [open, setOpen] = useState(false)
  useEffect(() => {
    void checkForUpdate().then(setRel)
  }, [])
  if (!rel) return null // schon aktuell / kein natives Gerät → kein Angebot
  return (
    <>
      <button onClick={() => setOpen(true)} className="shrink-0 rounded-lg px-2.5 py-1 text-xs font-semibold text-white" style={{ backgroundColor: 'var(--color-accent)' }}>
        Installieren
      </button>
      {open && <UpdateDialog release={rel} onClose={() => setOpen(false)} />}
    </>
  )
}

/** Feed-Gruppen (aufklappbar). Kategorie je Aktivität aus kind/refType abgeleitet. */
type FeedCat = 'termine' | 'aufgaben' | 'nachrichten'
const GROUPS: Array<{ key: FeedCat; title: string; icon: typeof CalendarDays; empty: string }> = [
  { key: 'termine', title: 'Neue Termine', icon: CalendarDays, empty: 'Gerade keine neuen Termine — der Kalender hat Pause. 📅' },
  { key: 'aufgaben', title: 'Neue Aufgaben', icon: ClipboardCheck, empty: 'Nichts Neues bei den Aufgaben — alles erledigt oder in Arbeit. 👍' },
  { key: 'nachrichten', title: 'Neue Mitteilungen', icon: Newspaper, empty: 'Noch keine neuen Mitteilungen. Schreib doch der Familie was! ✏️' },
]
function categoryOf(a: FeedActivityDto): FeedCat {
  if (a.kind === 'post' || a.kind === 'comment' || a.kind === 'poll') return 'nachrichten'
  if (a.refType === 'event') return 'termine'
  if (a.refType === 'app_release') return 'nachrichten' // App-Update-Ankündigung
  return 'aufgaben' // System: Aufgaben/Punkte/Taschengeld/Sparziele
}

export function FeedScreen({ members }: { members: MemberDto[] }): React.JSX.Element {
  const { member } = useAuth()
  const memberMap = useMemo(() => new Map(members.map((m) => [m.id, m])), [members])
  const [activities, setActivities] = useState<FeedActivityDto[]>([])
  const [draft, setDraft] = useState('')
  const [posting, setPosting] = useState(false)
  const [pollOpen, setPollOpen] = useState(false)

  const [loadError, setLoadError] = useState(false)
  const load = useCallback(async () => {
    try {
      setActivities((await api.getFeed()).activities)
      setLoadError(false)
    } catch {
      setLoadError(true)
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])
  useLive('feed', load)

  const canPost = member?.role === 'adult' // Mitteilungen nur Eltern (ADR-053)

  async function post(): Promise<void> {
    const body = draft.trim()
    if (!body) return
    setPosting(true)
    try {
      await api.createPost(body)
      setDraft('')
      await load()
    } finally {
      setPosting(false)
    }
  }

  return (
    <div className="grid h-full grid-cols-1 gap-4 overflow-auto p-3 md:grid-cols-[1fr_18rem] md:p-4">
      <div className="mx-auto w-full max-w-2xl">
        {/* Composer */}
        {canPost && member && (
          <div className="mb-4 flex gap-3 rounded-[var(--radius-card)] border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
            <Avatar name={member.name} color={member.color} size={40} src={member.avatarUrl} />
            <div className="flex-1">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Was willst du deiner Familie mitteilen?"
                rows={2}
                className="w-full resize-none rounded-lg border px-3 py-2 outline-none focus:border-[var(--color-accent)]"
                style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' }}
              />
              <div className="mt-2 flex items-center justify-between">
                <button onClick={() => setPollOpen(true)} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--color-accent)' }}>
                  <BarChart3 className="size-4" /> Abstimmung
                </button>
                <Button onClick={post} disabled={posting || !draft.trim()}>
                  Posten
                </Button>
              </div>
            </div>
          </div>
        )}

        {pollOpen && member && (
          <PollComposer onClose={() => setPollOpen(false)} onCreated={() => { setPollOpen(false); void load() }} />
        )}

        {loadError && <ErrorBanner message="Der Feed konnte nicht geladen werden." onRetry={() => void load()} />}

        {/* Stream — in aufklappbare Gruppen sortiert */}
        {activities.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-center">
            <img src="/icon.png" width={64} height={64} alt="" className="opacity-60" />
            <p className="text-lg font-medium" style={{ color: 'var(--color-ink)' }}>
              Noch ganz ruhig hier.
            </p>
            <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
              Teile den ersten Moment!
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {GROUPS.map((g) => {
              const groupItems = activities.filter((a) => categoryOf(a) === g.key)
              return (
                <FeedGroup
                  key={g.key}
                  title={g.title}
                  Icon={g.icon}
                  empty={g.empty}
                  items={groupItems}
                  defaultOpen={g.key === 'nachrichten' && groupItems.length > 0}
                  members={memberMap}
                  me={member}
                  onChanged={load}
                />
              )
            })}
          </div>
        )}
      </div>

      {/* Widgets */}
      <div className="hidden flex-col gap-4 md:flex">
        <UpcomingWidget />
        <ListsWidget />
      </div>
    </div>
  )
}

/** Aufklappbare Feed-Gruppe. „Neue Mitteilungen" klappt bei Inhalt automatisch auf. */
function FeedGroup({
  title,
  Icon,
  empty,
  items,
  defaultOpen = false,
  members,
  me,
  onChanged,
}: {
  title: string
  Icon: typeof CalendarDays
  empty: string
  items: FeedActivityDto[]
  defaultOpen?: boolean
  members: Map<string, MemberDto>
  me: MemberDto | null
  onChanged: () => void
}): React.JSX.Element {
  const [open, setOpen] = useState(defaultOpen)
  // Automatisch aufklappen, sobald (neue) Inhalte vorhanden sind.
  useEffect(() => {
    if (defaultOpen) setOpen(true)
  }, [defaultOpen])
  return (
    <section>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-[var(--radius-card)] border px-3 py-2 text-left"
        style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}
      >
        {open ? <ChevronDown className="size-4 shrink-0" style={{ color: 'var(--color-muted)' }} /> : <ChevronRight className="size-4 shrink-0" style={{ color: 'var(--color-muted)' }} />}
        <Icon className="size-4 shrink-0" style={{ color: 'var(--color-accent)' }} />
        <span className="font-medium" style={{ color: 'var(--color-ink)' }}>{title}</span>
        {items.length > 0 && <span className="text-sm" style={{ color: 'var(--color-muted)' }}>· {items.length}</span>}
      </button>
      {open && (
        items.length === 0 ? (
          <p className="px-3 py-3 text-sm" style={{ color: 'var(--color-muted)' }}>{empty}</p>
        ) : (
          <ul className="mt-2 flex flex-col gap-3">
            {items.map((a) => (
              <ActivityCard key={a.id} activity={a} members={members} me={me} onChanged={onChanged} />
            ))}
          </ul>
        )
      )}
    </section>
  )
}

function ActivityCard({
  activity,
  members,
  me,
  onChanged,
}: {
  activity: FeedActivityDto
  members: Map<string, MemberDto>
  me: MemberDto | null
  onChanged: () => void
}): React.JSX.Element {
  const dialogs = useDialogs()
  const [commentDraft, setCommentDraft] = useState('')
  const [showComment, setShowComment] = useState(false)
  const author = activity.actorId ? members.get(activity.actorId) : null
  // Kinder dürfen eigene Beiträge nur 15 Min lang löschen (muss zum Server passen).
  const childCanDelete =
    me?.role === 'child' && activity.actorId === me.id && Date.now() - Date.parse(activity.createdAt) < 15 * 60 * 1000
  const canDelete = me?.role === 'adult' || childCanDelete
  const canInteract = me?.role !== 'guest'

  async function like(): Promise<void> {
    await api.toggleLike(activity.id)
    onChanged()
  }
  async function comment(): Promise<void> {
    const body = commentDraft.trim()
    if (!body) return
    setCommentDraft('')
    await api.addComment(activity.id, body)
    onChanged()
  }
  async function remove(): Promise<void> {
    if (!(await dialogs.confirm({ title: 'Beitrag löschen', message: 'Diesen Beitrag löschen?', confirmLabel: 'Löschen', danger: true }))) return
    try {
      await api.deleteActivity(activity.id)
      onChanged()
    } catch {
      await dialogs.alert({
        title: 'Löschen nicht möglich',
        message:
          me?.role === 'child'
            ? 'Eigene Beiträge kannst du nur 15 Minuten lang löschen. Bitte eine erwachsene Person fragen.'
            : 'Bitte später erneut versuchen.',
      })
      onChanged()
    }
  }

  if (activity.kind === 'system') {
    const Icon = (activity.refType && REF_ICON[activity.refType]) || Sparkles
    return (
      <li className="flex items-center gap-3 rounded-[var(--radius-card)] border px-4 py-3 text-sm" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
        <Icon className="size-5 shrink-0" style={{ color: 'var(--color-accent)' }} />
        <span className="flex-1" style={{ color: 'var(--color-ink)' }}>
          {activity.body ?? '(Aktivität)'}
        </span>
        {activity.refType === 'app_release' && <ReleaseInstallButton />}
        <button
          onClick={like}
          disabled={!canInteract}
          aria-label="Gefällt mir"
          className="flex shrink-0 items-center gap-1 text-xs disabled:opacity-50"
          style={{ color: activity.likedByMe ? 'var(--color-danger)' : 'var(--color-muted)' }}
        >
          <Heart className="size-4" fill={activity.likedByMe ? 'var(--color-danger)' : 'none'} />
          {activity.likeCount > 0 && activity.likeCount}
        </button>
        <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
          {formatRelative(activity.createdAt)}
        </span>
      </li>
    )
  }

  return (
    <li className="rounded-[var(--radius-card)] border p-4" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
      <div className="flex items-center gap-3">
        {author ? <Avatar name={author.name} color={author.color} size={36} src={author.avatarUrl} /> : <div className="size-9 rounded-full" style={{ backgroundColor: 'var(--color-line)' }} />}
        <div className="flex-1">
          <div className="font-medium" style={{ color: author ? personTextColor(author.color) : 'var(--color-ink)' }}>
            {author?.name ?? 'Familie'}
          </div>
          <div className="text-xs" style={{ color: 'var(--color-muted)' }}>
            {formatRelative(activity.createdAt)}
          </div>
        </div>
        {canDelete && (
          <button onClick={remove} aria-label="Löschen" className="rounded p-1 hover:bg-black/5">
            <Trash2 className="size-4" style={{ color: 'var(--color-muted)' }} />
          </button>
        )}
      </div>

      {activity.body && <p className="mt-3 whitespace-pre-wrap" style={{ color: 'var(--color-ink)', fontWeight: activity.kind === 'poll' ? 600 : undefined }}>{activity.body}</p>}

      {activity.kind === 'poll' && activity.poll && (
        <PollBlock
          key={`${activity.poll.myVoteOptionIds.slice().sort().join(',')}|${activity.poll.closed}`}
          poll={activity.poll}
          members={members}
          me={me}
          onChanged={onChanged}
        />
      )}

      {/* Fußzeile */}
      <div className="mt-3 flex items-center gap-4 border-t pt-2 text-sm" style={{ borderColor: 'var(--color-line)' }}>
        <button onClick={like} disabled={!canInteract} className="flex items-center gap-1.5 disabled:opacity-50" style={{ color: activity.likedByMe ? 'var(--color-danger)' : 'var(--color-muted)' }}>
          <Heart className="size-4" fill={activity.likedByMe ? 'var(--color-danger)' : 'none'} />
          {activity.likeCount > 0 && activity.likeCount}
        </button>
        <button onClick={() => setShowComment((v) => !v)} className="flex items-center gap-1.5" style={{ color: 'var(--color-muted)' }}>
          <MessageCircle className="size-4" />
          {activity.comments.length > 0 && activity.comments.length}
        </button>
      </div>

      {/* Kommentare */}
      {activity.comments.length > 0 && (
        <ul className="mt-2 flex flex-col gap-2 pl-2">
          {activity.comments.map((c) => {
            const ca = c.actorId ? members.get(c.actorId) : null
            return (
              <li key={c.id} className="flex items-start gap-2">
                {ca && <Avatar name={ca.name} color={ca.color} size={24} src={ca.avatarUrl} />}
                <div className="rounded-lg px-3 py-1.5 text-sm" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <span className="font-medium" style={{ color: ca ? personTextColor(ca.color) : 'var(--color-ink)' }}>{ca?.name ?? 'Familie'}</span>{' '}
                  <span style={{ color: 'var(--color-ink)' }}>{c.body}</span>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {showComment && canInteract && (
        <div className="mt-2 flex items-center gap-2">
          <input
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && comment()}
            placeholder="Kommentieren…"
            className="flex-1 rounded-lg border px-3 py-1.5 text-sm outline-none focus:border-[var(--color-accent)]"
            style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-line)', color: 'var(--color-ink)' }}
          />
          <button onClick={comment} aria-label="Senden" className="rounded-lg p-2" style={{ backgroundColor: 'var(--color-accent)' }}>
            <Send className="size-4 text-white" />
          </button>
        </div>
      )}
    </li>
  )
}

/** Abstimmungs-Block in einem Feed-Beitrag (ADR-081): Optionen wählen, Ergebnisse offen. */
function PollBlock({ poll, members, me, onChanged }: { poll: PollDto; members: Map<string, MemberDto>; me: MemberDto | null; onChanged: () => void }): React.JSX.Element {
  // Auswahl aus dem Server-Stand initialisieren. Der Aufrufer remountet über einen
  // `key` (aus myVoteOptionIds + closed), sobald sich MEIN Server-Stand ändert →
  // kein useEffect nötig, und die laufende Auswahl bleibt beim Tippen erhalten.
  const [selected, setSelected] = useState<Set<string>>(() => new Set(poll.myVoteOptionIds))
  const [busy, setBusy] = useState(false)

  const isAdult = me?.role === 'adult'
  const canVote = !!me && me.role !== 'guest' && !poll.closed
  const max = poll.votesPerMember
  const single = max === 1
  const totalVoters = new Set(poll.options.flatMap((o) => o.voterIds)).size
  const maxCount = Math.max(1, ...poll.options.map((o) => o.voterIds.length))
  const changed = useMemo(() => [...selected].sort().join('|') !== [...poll.myVoteOptionIds].sort().join('|'), [selected, poll.myVoteOptionIds])

  function toggle(id: string): void {
    if (!canVote) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (single) { next.clear(); next.add(id); return next }
      if (next.has(id)) next.delete(id)
      else if (next.size < max) next.add(id)
      return next
    })
  }
  async function submit(): Promise<void> {
    setBusy(true)
    try { await api.votePoll(poll.id, [...selected]); onChanged() } finally { setBusy(false) }
  }
  async function closeVote(): Promise<void> {
    setBusy(true)
    try { await api.closePoll(poll.id); onChanged() } finally { setBusy(false) }
  }

  return (
    <div className="mt-3 flex flex-col gap-2">
      <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--color-muted)' }}>
        <BarChart3 className="size-4" style={{ color: 'var(--color-accent)' }} />
        <span>{poll.closed ? 'Beendet' : single ? 'Wähle eine Option' : `Wähle bis zu ${max} Optionen`} · {totalVoters} abgestimmt</span>
      </div>
      {poll.options.map((o) => {
        const on = selected.has(o.id)
        const count = o.voterIds.length
        const pct = Math.round((count / maxCount) * 100)
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => toggle(o.id)}
            disabled={!canVote}
            className="relative w-full overflow-hidden rounded-lg border px-3 py-2 text-left disabled:cursor-default"
            style={{ borderColor: on ? 'var(--color-accent)' : 'var(--color-line)', backgroundColor: 'var(--color-surface)' }}
          >
            <div className="absolute inset-y-0 left-0" style={{ width: `${pct}%`, backgroundColor: 'var(--color-accent)', opacity: 0.14 }} />
            <div className="relative flex items-center gap-2">
              <span className="flex size-4 shrink-0 items-center justify-center border" style={{ borderColor: on ? 'var(--color-accent)' : 'var(--color-line)', backgroundColor: on ? 'var(--color-accent)' : 'transparent', borderRadius: single ? '9999px' : '4px' }}>
                {on && <Check className="size-3 text-white" />}
              </span>
              <span className="flex-1 text-sm" style={{ color: 'var(--color-ink)' }}>{o.label}</span>
              <span className="text-xs font-medium" style={{ color: 'var(--color-muted)' }}>{count}</span>
            </div>
            {o.voterIds.length > 0 && (
              <div className="relative mt-1.5 flex flex-wrap gap-1 pl-6">
                {o.voterIds.map((vid) => {
                  const mm = members.get(vid)
                  return mm ? <Avatar key={vid} name={mm.name} color={mm.color} size={18} src={mm.avatarUrl} /> : null
                })}
              </div>
            )}
          </button>
        )
      })}
      <div className="flex items-center gap-2">
        {canVote && (
          <Button onClick={submit} disabled={busy || !changed}>
            {selected.size === 0 ? 'Stimme zurückziehen' : 'Abstimmen'}
          </Button>
        )}
        {poll.closed && (
          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-muted)' }}><Lock className="size-3" /> Beendet</span>
        )}
        {isAdult && !poll.closed && (
          <button onClick={closeVote} disabled={busy} className="ml-auto text-xs" style={{ color: 'var(--color-muted)' }}>Abstimmung schließen</button>
        )}
      </div>
    </div>
  )
}

/** Bodenständiges Bottom-Sheet/Modal für Feed-Dialoge. */
function FeedModal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }): React.JSX.Element {
  const backdrop = useBackdropClose(onClose)
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" {...backdrop}>
      <div className="max-h-[90vh] w-full max-w-md overflow-auto rounded-t-2xl border p-5 sm:rounded-2xl" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-ink)' }}>{title}</h2>
          <button onClick={onClose} aria-label="Schließen" className="rounded-lg p-1 hover:bg-black/5"><X className="size-5" style={{ color: 'var(--color-muted)' }} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}

/** Composer für eine neue Abstimmung (nur Eltern). */
function PollComposer({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }): React.JSX.Element {
  const [question, setQuestion] = useState('')
  const [options, setOptions] = useState<string[]>(['', ''])
  const [votesPerMember, setVotesPerMember] = useState(1)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filled = options.map((o) => o.trim()).filter((o) => o.length > 0)
  const maxVotes = Math.max(1, filled.length)

  function setOption(i: number, v: string): void {
    setOptions((prev) => prev.map((o, idx) => (idx === i ? v : o)))
  }
  function addOption(): void {
    setOptions((prev) => (prev.length >= 10 ? prev : [...prev, '']))
  }
  function removeOption(i: number): void {
    setOptions((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i)))
  }
  async function submit(): Promise<void> {
    setError(null)
    if (!question.trim()) { setError('Bitte eine Frage eingeben.'); return }
    if (filled.length < 2) { setError('Mindestens zwei Optionen angeben.'); return }
    setBusy(true)
    try {
      await api.createPoll({ question: question.trim(), options: filled, votesPerMember: Math.min(votesPerMember, filled.length) })
      onCreated()
    } catch {
      setError('Abstimmung konnte nicht erstellt werden.')
      setBusy(false)
    }
  }

  return (
    <FeedModal title="Neue Abstimmung" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Frage (z. B. Was essen wir am Sonntag?)" autoFocus className={fieldCls} style={fieldStyle} />
        <div className="flex flex-col gap-2">
          <span className="text-sm" style={{ color: 'var(--color-muted)' }}>Optionen</span>
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input value={o} onChange={(e) => setOption(i, e.target.value)} placeholder={`Option ${i + 1}`} className={fieldCls} style={fieldStyle} />
              {options.length > 2 && (
                <button onClick={() => removeOption(i)} aria-label="Entfernen" className="rounded p-1 hover:bg-black/5"><X className="size-4" style={{ color: 'var(--color-muted)' }} /></button>
              )}
            </div>
          ))}
          {options.length < 10 && (
            <button onClick={addOption} className="flex items-center gap-1 self-start text-sm" style={{ color: 'var(--color-accent)' }}><Plus className="size-4" /> Option hinzufügen</button>
          )}
        </div>
        <label className="flex items-center justify-between gap-2 text-sm" style={{ color: 'var(--color-ink)' }}>
          <span>Stimmen pro Person</span>
          <input type="number" min={1} max={maxVotes} value={votesPerMember} onChange={(e) => setVotesPerMember(Math.min(maxVotes, Math.max(1, Number(e.target.value) || 1)))} className="w-20 rounded-lg border px-2 py-1 text-right" style={fieldStyle} />
        </label>
        <p className="text-xs" style={{ color: 'var(--color-muted)' }}>Bei mehreren Stimmen darf jede Person entsprechend viele <b>verschiedene</b> Optionen ankreuzen.</p>
        {error && <p className="text-sm" style={{ color: 'var(--color-danger)' }}>{error}</p>}
        <div className="mt-1 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy}>Erstellen</Button>
        </div>
      </div>
    </FeedModal>
  )
}

/**
 * Kurzes „Heute / Morgen / Di 8.7." + Uhrzeit (bei ganztägig ohne Uhrzeit).
 * Mehrtägige Termine zeigen den ganzen Zeitraum („Mi 19.8. – Fr 21.8.");
 * läuft der Termin bereits, nur noch das Ende („Noch bis Fr 21.8.").
 */
function whenLabel(o: OccurrenceDto): string {
  const today = startOfDay(new Date())
  const fmtDay = (d: Date): string =>
    isSameDay(d, today)
      ? 'heute'
      : isSameDay(d, addDays(today, 1))
        ? 'morgen'
        : d.toLocaleDateString('de-DE', { weekday: 'short', day: 'numeric', month: 'numeric' })
  const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)
  const { startDay, endDay } = occurrenceDayRange(o)
  if (endDay.getTime() > startDay.getTime()) {
    if (startDay.getTime() < today.getTime()) return `Noch bis ${fmtDay(endDay)}`
    return `${cap(fmtDay(startDay))} – ${fmtDay(endDay)}`
  }
  const day = cap(fmtDay(startDay))
  return o.allDay ? day : `${day} ${formatTime(o.occurrenceStart)}`
}

/**
 * Anstehende Ereignisse (Feed-Widget): ganztägige Termine von **heute** plus die
 * Termine der **nächsten 7 Tage**, jeweils nur, wenn sie den angemeldeten User
 * **betreffen** (teilnehmend / nur Info, selbst angelegt, oder allgemeiner Termin
 * ohne Teilnehmerliste). Erwachsene sehen serverseitig zwar alles — hier wird auf
 * „betrifft mich" eingegrenzt.
 */
function UpcomingWidget(): React.JSX.Element {
  const { member } = useAuth()
  const [occ, setOcc] = useState<OccurrenceDto[]>([])
  const load = useCallback(async () => {
    const from = startOfDay(new Date())
    const to = addDays(from, 8) // heute + nächste 7 Tage
    setOcc(await api.listOccurrences(from.toISOString(), to.toISOString()))
  }, [])
  useEffect(() => {
    void load()
  }, [load])
  useLive('calendar', load)

  const meId = member?.id
  const now = Date.now()
  const today = startOfDay(new Date())
  const shown = occ
    .filter((o) => {
      const concernsMe =
        o.attendees.length === 0 ||
        (meId != null && (o.createdBy === meId || o.attendees.some((a) => a.memberId === meId)))
      if (!concernsMe) return false
      const { startDay, endDay } = occurrenceDayRange(o)
      // Ganztägige und mehrtägige Termine bleiben bis zu ihrem letzten Tag
      // sichtbar (vorher verschwanden laufende mehrtägige Termine ab Tag 2).
      if (o.allDay || endDay.getTime() > startDay.getTime()) return endDay.getTime() >= today.getTime()
      return new Date(o.occurrenceStart).getTime() >= now // eintägig mit Uhrzeit: nur anstehende
    })
    .slice(0, 8)

  return (
    <Widget title="Anstehende Ereignisse">
      {shown.length === 0 ? (
        <Empty>Keine Termine</Empty>
      ) : (
        shown.map((o) => (
          <div key={o.id + o.occurrenceStart} className="flex items-baseline gap-2 py-1 text-sm">
            <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: 'var(--color-accent)' }} />
            <span className="flex-1 truncate" style={{ color: 'var(--color-ink)' }}>{o.title}</span>
            <span className="shrink-0 text-xs" style={{ color: 'var(--color-muted)' }}>{whenLabel(o)}</span>
          </div>
        ))
      )}
    </Widget>
  )
}

function ListsWidget(): React.JSX.Element {
  const [lists, setLists] = useState<ListDto[]>([])
  const load = useCallback(async () => setLists(await api.listLists()), [])
  useEffect(() => {
    void load()
  }, [load])
  useLive('lists', load)
  return (
    <Widget title="Listen">
      {lists.length === 0 ? (
        <Empty>Keine Listen</Empty>
      ) : (
        lists.slice(0, 5).map((l) => (
          <div key={l.id} className="flex items-center gap-2 py-1 text-sm">
            <span className="flex-1 truncate" style={{ color: 'var(--color-ink)' }}>{l.name}</span>
            <span className="text-xs" style={{ color: 'var(--color-muted)' }}>{l.openCount} offen</span>
          </div>
        ))
      )}
    </Widget>
  )
}

function Widget({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <section className="rounded-[var(--radius-card)] border p-3" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-line)' }}>
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-muted)' }}>{title}</h2>
      {children}
    </section>
  )
}
function Empty({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <p className="py-2 text-sm" style={{ color: 'var(--color-muted)' }}>{children}</p>
}
